import { useEffect, useMemo, useState } from "react";
import { MapPinned, Plus, Building2, Users, Inbox, CornerDownRight, Pencil, Trash2, Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AdminAPI, PublicAPI } from "@/lib/api";
import { toast } from "sonner";

const LEVEL_LABEL = {
  KANTOR_PUSAT: "Kantor Pusat", KANTOR_WILAYAH: "Kantor Wilayah", KANTOR_DAERAH: "Kantor Daerah",
  KANTOR_CABANG: "Kantor Cabang", KANTOR_RANTING: "Ranting", UNIT_GERAI_OUTLET: "Gerai / Unit",
};
const CHILD_LEVEL = {
  KANTOR_PUSAT: "KANTOR_WILAYAH", KANTOR_WILAYAH: "KANTOR_DAERAH", KANTOR_DAERAH: "KANTOR_CABANG",
  KANTOR_CABANG: "KANTOR_RANTING", KANTOR_RANTING: "UNIT_GERAI_OUTLET",
};
const levelBadge = {
  KANTOR_PUSAT: "bg-forest/10 text-forest", KANTOR_WILAYAH: "bg-forest/15 text-forest",
  KANTOR_DAERAH: "bg-amber-warm/15 text-amber-700",
  KANTOR_CABANG: "bg-sage/20 text-forest", KANTOR_RANTING: "bg-terracotta/10 text-terracotta",
  UNIT_GERAI_OUTLET: "bg-stone-soft/40 text-ink/75",
};

export default function AdminWilayah() {
  const [orgs, setOrgs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [parentId, setParentId] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  // Tagging wilayah administratif saat membuat Daerah/Cabang.
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [provinceId, setProvinceId] = useState("");
  const [regencyId, setRegencyId] = useState("");
  const [editOrg, setEditOrg] = useState(null);   // org yang sedang diedit
  const [editName, setEditName] = useState("");
  const [delOrg, setDelOrg] = useState(null);     // org yang akan dihapus
  const [busy, setBusy] = useState(false);

  const load = () =>
    AdminAPI.organizationsManage().then((rows) => setOrgs(rows || [])).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { load(); PublicAPI.provinces().then(setProvinces).catch(() => setProvinces([])); }, []);

  // Induk yang valid = organisasi yang levelnya masih punya level anak (bukan gerai).
  const parentOptions = useMemo(() => orgs.filter((o) => CHILD_LEVEL[o.level]), [orgs]);
  const selectedParent = orgs.find((o) => String(o.id) === String(parentId));
  const childLevel = selectedParent ? CHILD_LEVEL[selectedParent.level] : null;
  // Wilayah = Provinsi, Daerah = Kota/Kabupaten (model bisnis 6 level).
  const needProvince = childLevel === "KANTOR_WILAYAH" || childLevel === "KANTOR_DAERAH";
  const needRegency = childLevel === "KANTOR_DAERAH";

  const pickParent = (val) => {
    setParentId(val);
    setProvinceId(""); setRegencyId(""); setRegencies([]);
  };
  const pickProvince = (val) => {
    setProvinceId(val); setRegencyId(""); setRegencies([]);
    PublicAPI.regencies(Number(val)).then(setRegencies).catch(() => setRegencies([]));
  };

  const resetForm = () => { setName(""); setParentId(""); setProvinceId(""); setRegencyId(""); setRegencies([]); };

  const submit = async () => {
    if (!parentId || !name.trim()) return toast.error("Pilih induk & isi nama wilayah");
    if (needProvince && !provinceId) return toast.error("Pilih provinsi");
    if (needRegency && !regencyId) return toast.error("Pilih kota/kabupaten");
    setSaving(true);
    try {
      const org = await AdminAPI.createOrganization({
        name: name.trim(),
        parent_id: Number(parentId),
        province_id: provinceId ? Number(provinceId) : undefined,
        regency_id: regencyId ? Number(regencyId) : undefined,
      });
      toast.success(`${LEVEL_LABEL[org.level]} "${org.name}" dibuat`);
      setAddOpen(false); resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal membuat wilayah");
    } finally { setSaving(false); }
  };

  const byLevel = (lvl) => orgs.filter((o) => o.level === lvl);

  const openEdit = (o) => { setEditOrg(o); setEditName(o.name); };
  const submitEdit = async () => {
    if (!editName.trim()) return toast.error("Nama wilayah wajib diisi");
    setBusy(true);
    try {
      await AdminAPI.updateOrganization(editOrg.id, { name: editName.trim() });
      toast.success("Wilayah diperbarui");
      setEditOrg(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal memperbarui wilayah");
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await AdminAPI.deleteOrganization(delOrg.id);
      toast.success(`Wilayah "${delOrg.name}" dihapus`);
      setDelOrg(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menghapus wilayah");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Manajemen Wilayah</h1>
          <p className="text-ink/75 mt-2 text-sm">Bangun hierarki kantor: Wilayah → Daerah → Cabang → Ranting → Gerai. Wilayah baru bisa didaftari admin lewat alur persetujuan berjenjang.</p>
        </div>
        <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" onClick={() => setAddOpen(true)} data-testid="wilayah-add">
          <Plus size={15} /> Tambah Wilayah
        </Button>
      </div>

      {/* Ringkasan per level */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {Object.keys(LEVEL_LABEL).map((lvl) => (
          <Card key={lvl} className="rounded-xl border-stone-soft p-5">
            <div className={`w-9 h-9 rounded-lg grid place-items-center ${levelBadge[lvl]}`}><Building2 size={17} /></div>
            <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold mt-3">{LEVEL_LABEL[lvl]}</div>
            <div className="font-display font-bold text-2xl text-ink mt-1">{byLevel(lvl).length}</div>
          </Card>
        ))}
      </div>

      <Card className="rounded-xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink mb-4">Daftar Wilayah</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                <TableHead className="text-[10px] uppercase tracking-wider">Nama Wilayah</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Level</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Induk</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Wilayah Adm.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-center">Sub-wilayah</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-center">Admin</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgs.map((o) => (
                <TableRow key={o.id} className="border-stone-soft hover:bg-cream/30" data-testid={`org-row-${o.id}`}>
                  <TableCell className="font-semibold text-sm text-ink">{o.name}</TableCell>
                  <TableCell><Badge className={`${levelBadge[o.level]} hover:${levelBadge[o.level]} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{LEVEL_LABEL[o.level]}</Badge></TableCell>
                  <TableCell className="text-sm text-ink/75">{o.parent_name || "—"}</TableCell>
                  <TableCell className="text-sm text-ink/75">{o.regency_name || o.province_name || "—"}</TableCell>
                  <TableCell className="text-center text-sm">{o.child_count}</TableCell>
                  <TableCell className="text-center">
                    {Number(o.admin_count) > 0
                      ? <Badge className="bg-sage/20 text-forest hover:bg-sage/20 border-0 rounded-full text-[10px]"><Users size={10} className="mr-1" />{o.admin_count}</Badge>
                      : <Badge className="bg-amber-warm/15 text-amber-700 hover:bg-amber-warm/15 border-0 rounded-full text-[10px]">Belum ada</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {o.manageable ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="rounded-lg hover:bg-cream h-8 w-8" onClick={() => openEdit(o)} data-testid={`org-edit-${o.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" className="rounded-lg hover:bg-terracotta/10 text-ink/60 hover:text-terracotta h-8 w-8" onClick={() => setDelOrg(o)} data-testid={`org-delete-${o.id}`}><Trash2 size={14} /></Button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-ink/40" title="Hanya wilayah di bawah Anda yang bisa diubah"><Lock size={11} /> —</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loaded && orgs.length === 0 && (
            <div className="py-12 text-center"><Inbox className="mx-auto text-ink/30" size={28} /><p className="text-sm text-ink/55 mt-2">Belum ada wilayah</p></div>
          )}
        </div>
      </Card>

      {/* Dialog Tambah Wilayah */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Tambah Wilayah</DialogTitle>
            <DialogDescription>Pilih wilayah induk — level anak ditentukan otomatis (Wilayah → Daerah → Cabang → Ranting → Gerai).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Wilayah Induk</Label>
              <Select value={parentId} onValueChange={pickParent}>
                <SelectTrigger className="rounded-lg" data-testid="wilayah-parent"><SelectValue placeholder="Pilih induk..." /></SelectTrigger>
                <SelectContent>
                  {parentOptions.map((o) => (
                    <SelectItem key={o.id} value={String(o.id)}>{o.name} · {LEVEL_LABEL[o.level]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {childLevel && (
              <div className="flex items-center gap-2 text-xs text-forest bg-forest/5 rounded-lg p-2.5">
                <CornerDownRight size={14} /> Akan dibuat sebagai <b>{LEVEL_LABEL[childLevel]}</b> di bawah {selectedParent?.name}
              </div>
            )}
            {needProvince && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-ink/55">Provinsi</Label>
                <Select value={provinceId} onValueChange={pickProvince}>
                  <SelectTrigger className="rounded-lg" data-testid="wilayah-province"><SelectValue placeholder="Pilih provinsi..." /></SelectTrigger>
                  <SelectContent>
                    {provinces.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {needRegency && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-ink/55">Kota / Kabupaten</Label>
                <Select value={regencyId} onValueChange={setRegencyId} disabled={!provinceId}>
                  <SelectTrigger className="rounded-lg" data-testid="wilayah-regency"><SelectValue placeholder={provinceId ? "Pilih kota/kabupaten..." : "Pilih provinsi dulu"} /></SelectTrigger>
                  <SelectContent>
                    {regencies.map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Wilayah</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="cth. Kantor Cabang Jakarta Barat" className="rounded-lg" data-testid="wilayah-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setAddOpen(false); resetForm(); }}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submit} disabled={saving} data-testid="wilayah-submit">
              {saving ? "Menyimpan…" : "Buat Wilayah"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Edit Wilayah */}
      <Dialog open={!!editOrg} onOpenChange={(v) => !v && setEditOrg(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Wilayah</DialogTitle>
            <DialogDescription>{editOrg ? `${LEVEL_LABEL[editOrg.level]} · induk: ${editOrg.parent_name || "—"}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 mt-2">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Wilayah</Label>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-lg" data-testid="wilayah-edit-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditOrg(null)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submitEdit} disabled={busy} data-testid="wilayah-edit-submit">
              {busy ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Konfirmasi Hapus Wilayah */}
      <AlertDialog open={!!delOrg} onOpenChange={(v) => !v && setDelOrg(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Hapus wilayah ini?</AlertDialogTitle>
            <AlertDialogDescription>
              {delOrg ? <>Wilayah <b>{delOrg.name}</b> akan dihapus permanen. Wilayah harus tidak punya sub-wilayah & tidak ada admin terkait.</> : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-terracotta hover:bg-terracotta/90" onClick={confirmDelete} disabled={busy} data-testid="wilayah-delete-confirm">
              {busy ? "Menghapus…" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
